<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div class="p-2 bg-[#22282F]/10 rounded-lg">
            <UserCircle class="w-7 h-7 sm:w-8 sm:h-8 text-[#22282F]" />
          </div>
          Mon profil
        </h1>
        <p class="text-gray-500 mt-2">Vos informations et la sécurité de votre compte</p>
      </div>

      <!-- Carte profil -->
      <div class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
        <div class="bg-gradient-to-r from-[#22282F] to-[#2a3139] px-6 py-6 flex items-center gap-4">
          <div class="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center text-white text-2xl font-semibold flex-shrink-0">
            {{ initials }}
          </div>
          <div class="min-w-0">
            <p class="text-lg font-semibold text-white truncate">{{ user?.full_name || user?.username }}</p>
            <p class="text-sm text-white/70 truncate">{{ user?.role?.nom || '—' }}</p>
          </div>
        </div>

        <dl class="divide-y divide-gray-100">
          <div class="px-6 py-4 flex items-start gap-3">
            <UserIcon class="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <dt class="text-xs text-gray-400">Nom d'utilisateur</dt>
              <dd class="text-sm text-gray-900 font-medium">{{ user?.username || '—' }}</dd>
            </div>
          </div>
          <div class="px-6 py-4 flex items-start gap-3">
            <Mail class="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <dt class="text-xs text-gray-400">Email</dt>
              <dd class="text-sm text-gray-900 font-medium">{{ user?.email || '—' }}</dd>
            </div>
          </div>
          <div class="px-6 py-4 flex items-start gap-3">
            <ShieldCheck class="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <dt class="text-xs text-gray-400">Rôle</dt>
              <dd class="text-sm text-gray-900 font-medium">{{ user?.role?.nom || '—' }}</dd>
            </div>
          </div>
          <div v-if="scopeLabel" class="px-6 py-4 flex items-start gap-3">
            <Building2 class="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <dt class="text-xs text-gray-400">{{ scopeType }}</dt>
              <dd class="text-sm text-gray-900 font-medium">{{ scopeLabel }}</dd>
            </div>
          </div>
        </dl>
      </div>

      <!-- Section sécurité / 2FA -->
      <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div class="flex items-start justify-between gap-4 flex-wrap">
          <div class="flex items-start gap-4">
            <div :class="['p-3 rounded-xl', enabled ? 'bg-green-100' : 'bg-gray-100']">
              <ShieldCheck v-if="enabled" class="w-6 h-6 text-green-600" />
              <ShieldAlert v-else class="w-6 h-6 text-gray-500" />
            </div>
            <div>
              <h2 class="text-lg font-semibold text-gray-900">Double authentification (2FA)</h2>
              <p class="text-sm text-gray-500 mt-0.5 max-w-md">
                Ajoute une étape de vérification par code temporaire (Google Authenticator, Authy…) à chaque connexion.
              </p>
              <div class="mt-2">
                <span v-if="loadingStatus" class="inline-flex items-center gap-1.5 text-xs text-gray-400">
                  <div class="w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
                  Vérification…
                </span>
                <span
                  v-else
                  :class="[
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                    enabled ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  ]"
                >
                  <span :class="['w-1.5 h-1.5 rounded-full', enabled ? 'bg-green-500' : 'bg-amber-500']" />
                  {{ enabled ? 'Activée' : 'Non configurée' }}
                </span>
                <span v-if="enabled && confirmedAt" class="text-xs text-gray-400 ml-2">
                  depuis le {{ formatDate(confirmedAt) }}
                </span>
              </div>
            </div>
          </div>

          <button
            v-if="!loadingStatus && mode === 'idle'"
            @click="enabled ? startDisable() : startEnable()"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              enabled
                ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                : 'bg-[#22282F] text-white hover:bg-[#2a3139]'
            ]"
          >
            {{ enabled ? 'Désactiver' : 'Activer' }}
          </button>
        </div>

        <!-- Erreur -->
        <div v-if="error" class="mt-5 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p class="text-sm text-red-800">{{ error }}</p>
        </div>

        <!-- Mode activation : QR + confirmation -->
        <div v-if="mode === 'enable'" class="mt-6 pt-6 border-t border-gray-200">
          <div v-if="loadingQr" class="flex flex-col items-center justify-center py-8">
            <div class="w-10 h-10 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin mb-3" />
            <p class="text-gray-500 text-sm">Génération du QR code…</p>
          </div>
          <div v-else class="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
            <div class="p-3 bg-white border border-gray-200 rounded-xl shadow-sm flex-shrink-0">
              <img v-if="qrCode" :src="qrCode" alt="QR code 2FA" class="w-40 h-40" />
            </div>
            <div class="flex-1 w-full">
              <p class="text-sm text-gray-700 mb-1">
                Scannez ce QR code avec votre application d'authentification, puis saisissez le code généré.
              </p>
              <button
                v-if="secret"
                type="button"
                @click="showSecret = !showSecret"
                class="text-xs text-[#22282F] hover:underline flex items-center gap-1 mb-3"
              >
                <KeyRound class="w-3.5 h-3.5" />
                {{ showSecret ? 'Masquer la clé' : 'Afficher la clé manuelle' }}
              </button>
              <div
                v-if="showSecret && secret"
                class="mb-3 flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
              >
                <code class="text-xs text-gray-800 break-all">{{ secret }}</code>
                <button type="button" @click="copySecret" class="text-gray-400 hover:text-[#22282F]" title="Copier">
                  <Copy class="w-4 h-4" />
                </button>
              </div>

              <form @submit.prevent="confirmEnable" class="space-y-4">
                <div class="flex items-center gap-2">
                  <input
                    v-for="(_, i) in 6"
                    :key="i"
                    :value="otp[i]"
                    @input="handleInput($event, i)"
                    @keydown="onKeydown($event, i)"
                    @paste="onPaste($event)"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    type="tel"
                    :disabled="busy"
                    class="w-10 h-12 sm:w-11 sm:h-12 text-center text-base font-semibold rounded-lg border border-gray-300 bg-gray-50 text-gray-900 focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/15 transition-all disabled:opacity-50"
                    :ref="el => setOtpRef(el, i)"
                    maxlength="1"
                  />
                </div>
                <div class="flex gap-2">
                  <button
                    type="submit"
                    :disabled="busy || !isComplete"
                    class="px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm font-medium hover:bg-[#2a3139] transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <div v-if="busy" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Confirmer l'activation
                  </button>
                  <button type="button" @click="cancel" class="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <!-- Mode désactivation : code requis -->
        <div v-if="mode === 'disable'" class="mt-6 pt-6 border-t border-gray-200">
          <p class="text-sm text-gray-700 mb-4">
            Saisissez un code de votre application d'authentification pour confirmer la désactivation.
          </p>
          <form @submit.prevent="confirmDisable" class="space-y-4">
            <div class="flex items-center gap-2">
              <input
                v-for="(_, i) in 6"
                :key="i"
                :value="otp[i]"
                @input="handleInput($event, i)"
                @keydown="onKeydown($event, i)"
                @paste="onPaste($event)"
                inputmode="numeric"
                pattern="[0-9]*"
                type="tel"
                :disabled="busy"
                class="w-10 h-12 sm:w-11 sm:h-12 text-center text-base font-semibold rounded-lg border border-gray-300 bg-gray-50 text-gray-900 focus:outline-none focus:border-[#8C0004] focus:ring-2 focus:ring-[#8C0004]/15 transition-all disabled:opacity-50"
                :ref="el => setOtpRef(el, i)"
                maxlength="1"
              />
            </div>
            <div class="flex gap-2">
              <button
                type="submit"
                :disabled="busy || !isComplete"
                class="px-4 py-2 bg-[#8C0004] text-white rounded-lg text-sm font-medium hover:bg-[#a30005] transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <div v-if="busy" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Désactiver la 2FA
              </button>
              <button type="button" @click="cancel" class="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import {
  UserCircle, User as UserIcon, Mail, ShieldCheck, ShieldAlert, Building2,
  AlertCircle, KeyRound, Copy,
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { useAuthStore } from '@/store/auth'
import { useToast } from '@/utils/toast'

const store = useAuthStore()
const toast = useToast()
const user = computed(() => store.user)

const initials = computed(() => {
  const name = user.value?.full_name || user.value?.username || ''
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?'
})

const scopeType = computed(() => (user.value?.ambassade ? 'Ambassade' : 'Direction'))
const scopeLabel = computed(() => user.value?.ambassade?.nom || user.value?.direction?.nom || '')

const enabled = ref(false)
const confirmedAt = ref<string | null>(null)
const loadingStatus = ref(true)

type Mode = 'idle' | 'enable' | 'disable'
const mode = ref<Mode>('idle')
const loadingQr = ref(false)
const busy = ref(false)
const error = ref('')

const qrCode = ref<string | null>(null)
const secret = ref<string | null>(null)
const showSecret = ref(false)

const otp = ref<string[]>(Array(6).fill(''))
const otpRefs = ref<Array<HTMLInputElement | null>>([])
const isComplete = computed(() => otp.value.every((d) => /^\d$/.test(d)))

onMounted(loadStatus)

async function loadStatus() {
  loadingStatus.value = true
  const s = await store.status2FA()
  enabled.value = s.enabled
  confirmedAt.value = s.confirmedAt
  loadingStatus.value = false
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
  } catch {
    return iso
  }
}

async function startEnable() {
  error.value = ''
  resetCode()
  mode.value = 'enable'
  loadingQr.value = true
  const result = await store.setup2FA()
  loadingQr.value = false
  if (result.success) {
    qrCode.value = result.qrCode || null
    secret.value = result.secret || null
    await nextTick()
    focusIndex(0)
  } else {
    error.value = result.error || 'Impossible de générer le QR code'
    mode.value = 'idle'
  }
}

function startDisable() {
  error.value = ''
  resetCode()
  mode.value = 'disable'
  nextTick(() => focusIndex(0))
}

function cancel() {
  mode.value = 'idle'
  error.value = ''
  resetCode()
}

async function confirmEnable() {
  const code = otp.value.join('')
  if (!/^\d{6}$/.test(code)) return
  busy.value = true
  error.value = ''
  const result = await store.confirm2FA(code)
  busy.value = false
  if (result.status === 'success') {
    toast.success('2FA activée', 'La double authentification est maintenant active.')
    mode.value = 'idle'
    await loadStatus()
  } else {
    error.value = (result as any).error || 'Code incorrect'
    resetCode()
  }
}

async function confirmDisable() {
  const code = otp.value.join('')
  if (!/^\d{6}$/.test(code)) return
  busy.value = true
  error.value = ''
  const result = await store.disable2FA(code)
  busy.value = false
  if (result.success) {
    toast.success('2FA désactivée', 'La double authentification a été désactivée.')
    mode.value = 'idle'
    await loadStatus()
  } else {
    error.value = result.error || 'Code incorrect'
    resetCode()
  }
}

async function copySecret() {
  if (secret.value) {
    try {
      await navigator.clipboard.writeText(secret.value)
      toast.success('Copié', 'La clé a été copiée dans le presse-papiers.')
    } catch { /* noop */ }
  }
}

// --- Gestion des 6 cases ---
function setOtpRef(el: any, idx: number) {
  otpRefs.value[idx] = (el as HTMLInputElement) || null
}
function focusIndex(i: number) {
  const el = otpRefs.value[i]
  el?.focus()
  el?.select()
}
function distribute(digits: string) {
  const ds = digits.replace(/\D/g, '').slice(0, 6)
  for (let i = 0; i < 6; i++) otp.value[i] = ds[i] || ''
  const firstEmpty = otp.value.findIndex((d) => !d)
  focusIndex(firstEmpty === -1 ? 5 : firstEmpty)
}
function handleInput(e: Event, idx: number) {
  const target = e.target as HTMLInputElement
  const val = (target.value || '').replace(/\D/g, '')
  if (val.length > 1) { distribute(val); return }
  otp.value[idx] = val
  if (val && idx < 5) focusIndex(idx + 1)
}
function onKeydown(e: KeyboardEvent, idx: number) {
  if (e.key === 'Backspace') {
    if (otp.value[idx]) otp.value[idx] = ''
    else if (idx > 0) { otp.value[idx - 1] = ''; focusIndex(idx - 1) }
    e.preventDefault()
  } else if (e.key === 'ArrowLeft' && idx > 0) { focusIndex(idx - 1); e.preventDefault() }
  else if (e.key === 'ArrowRight' && idx < 5) { focusIndex(idx + 1); e.preventDefault() }
}
function onPaste(e: ClipboardEvent) {
  const digits = (e.clipboardData?.getData('text') || '').replace(/\D/g, '')
  if (!digits) return
  e.preventDefault()
  distribute(digits)
}
function resetCode() {
  otp.value = Array(6).fill('')
}
</script>
